import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Column, ColumnPriority } from '@/components/table/types';

/**
 * Internal column with visibility metadata
 */
interface ColumnWithVisibility<TData> extends Column<TData> {
  /** Computed priority for hiding order */
  priority: number;
  /** Whether column is currently visible */
  isVisible: boolean;
  /** Computed width after responsive calculations */
  computedWidth?: number;
  /** Whether column is compressed (expandable column at minWidth) */
  isCompressed?: boolean;
  /** Original index in columns array */
  originalIndex: number;
}

/**
 * Options for responsive column behavior
 */
export interface UseResponsiveColumnsOptions {
  /** Enable responsive column hiding */
  enabled?: boolean;
  /** Debounce delay for resize events (ms) */
  debounceDelay?: number;
  /** Reserved width for non-column elements (px) - checkboxes, actions, padding */
  reservedWidth?: number;
}

/**
 * Result of responsive column calculations
 */
export interface ResponsiveColumnsResult<TData> {
  /** Columns that should be rendered (visible) */
  visibleColumns: Column<TData>[];
  /** All columns with visibility metadata */
  columnsWithVisibility: ColumnWithVisibility<TData>[];
  /** Current container width */
  containerWidth: number;
  /** Whether responsive mode is active (any columns hidden) */
  isResponsive: boolean;
}

/**
 * Hook to manage responsive column visibility based on container width.
 *
 * Implements two-phase responsive behavior:
 * 1. Phase 1: Compress expandable columns to their minWidth
 * 2. Phase 2: Hide columns by priority (low priority first)
 *
 * @param columns - Array of column configurations
 * @param containerRef - Ref to table container element
 * @param options - Configuration options
 * @returns Visible columns and responsive state
 *
 * @example
 * const tableRef = useRef<HTMLDivElement>(null);
 * const { visibleColumns, isResponsive } = useResponsiveColumns(
 *   columns,
 *   tableRef,
 *   { enabled: true }
 * );
 */
export function useResponsiveColumns<TData>(
  columns: Column<TData>[],
  containerRef: React.RefObject<HTMLElement | null>,
  options: UseResponsiveColumnsOptions = {}
): ResponsiveColumnsResult<TData> {
  const {
    enabled = true,
    debounceDelay = 150,
    reservedWidth = 80, // Checkbox (20px) + Actions (40px) + padding (20px)
  } = options;

  const [containerWidth, setContainerWidth] = useState(0);
  const resizeTimeoutRef = useRef<number | undefined>(undefined);

  // Monitor container width changes with ResizeObserver
  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      // Debounce to prevent excessive recalculations
      if (resizeTimeoutRef.current !== undefined) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = window.setTimeout(() => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          setContainerWidth(width);
        }
      }, debounceDelay);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeoutRef.current !== undefined) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [enabled, containerRef, debounceDelay]);

  // Calculate visible columns based on container width
  const result = useMemo<ResponsiveColumnsResult<TData>>(() => {
    if (!enabled || containerWidth === 0) {
      // Responsive disabled or initial render - show all columns
      return {
        visibleColumns: columns,
        columnsWithVisibility: columns.map((col, idx) => ({
          ...col,
          priority: inferColumnPriority(col),
          isVisible: true,
          originalIndex: idx,
        })),
        containerWidth,
        isResponsive: false,
      };
    }

    // Available width for columns (exclude reserved width for UI elements)
    // Add extra margin to hide columns BEFORE they get compressed
    const safetyMargin = 100; // Extra space to ensure early hiding
    const availableWidth = containerWidth - reservedWidth - safetyMargin;

    // Add metadata to columns
    const columnsWithMetadata: ColumnWithVisibility<TData>[] = columns.map(
      (col, idx) => {
        const priority = col.responsive?.priority ?? inferColumnPriority(col);

        // DEV mode validation: Warn about invalid priority values
        if (
          // eslint-disable-next-line no-undef
          process.env.NODE_ENV === 'development' &&
          col.responsive?.priority !== undefined
        ) {
          const validPriorities = Object.values(ColumnPriority) as number[];
          if (!validPriorities.includes(col.responsive.priority)) {
            console.warn(
              `Column "${String(col.id)}" has invalid responsive priority: ${col.responsive.priority}.\n` +
                `Expected ColumnPriority enum values: CRITICAL(10), HIGH(8), MEDIUM_HIGH(6), MEDIUM(4), LOW(2).\n` +
                `Auto-inferring priority as ${priority}.`
            );
          }
        }

        return {
          ...col,
          priority,
          isVisible: true,
          originalIndex: idx,
        };
      }
    );

    // Phase 0: Filter by minViewportWidth BEFORE space calculation
    // This ensures columns hide based on viewport breakpoints first
    const viewportFilteredColumns = columnsWithMetadata.map(col => {
      // Hide if container width is less than column's minViewportWidth
      if (
        col.responsive?.minViewportWidth &&
        containerWidth < col.responsive.minViewportWidth
      ) {
        return { ...col, isVisible: false };
      }
      return col;
    });

    // Phase 1: Calculate compressed state (expandable columns at minWidth)
    // Only calculate for visible columns after viewport filtering
    const visibleAfterViewportFilter = viewportFilteredColumns.filter(
      col => col.isVisible
    );
    const { totalWidth: compressedWidth, columns: compressedColumns } =
      compressExpandableColumns(visibleAfterViewportFilter);

    if (compressedWidth <= availableWidth) {
      // All columns fit when compressed - return visible columns
      // Map back to original columns, preserving hidden state from viewport filter
      const hiddenByViewport = viewportFilteredColumns.filter(
        col => !col.isVisible
      );
      const visibleColumns = compressedColumns.map(
        col => columns[col.originalIndex]
      );

      return {
        visibleColumns,
        columnsWithVisibility: viewportFilteredColumns.map(col => {
          const compressed = compressedColumns.find(
            c => c.originalIndex === col.originalIndex
          );
          return compressed || col;
        }),
        containerWidth,
        isResponsive:
          compressedColumns.some(col => col.isCompressed) ||
          hiddenByViewport.length > 0,
      };
    }

    // Phase 2: Hide columns by priority until fits
    const { columns: finalColumns, isResponsive } = hideColumnsByPriority(
      compressedColumns,
      availableWidth
    );

    // Merge viewport-hidden columns back into final result
    const allColumnsWithVisibility = viewportFilteredColumns.map(col => {
      const final = finalColumns.find(
        c => c.originalIndex === col.originalIndex
      );
      return final || col; // Use viewport-filtered state if not in finalColumns
    });

    return {
      visibleColumns: allColumnsWithVisibility
        .filter(col => col.isVisible)
        .map(col => columns[col.originalIndex]),
      columnsWithVisibility: allColumnsWithVisibility,
      containerWidth,
      isResponsive,
    };
  }, [columns, containerWidth, enabled, reservedWidth]);

  return result;
}

/**
 * Infers column priority from its properties.
 * Used when explicit priority not provided.
 */
function inferColumnPriority<TData>(column: Column<TData>): number {
  // Explicit priority takes precedence
  if (column.responsive?.priority !== undefined) {
    return column.responsive.priority;
  }

  // Sticky columns must never be hidden (defeats the pin-on-scroll purpose)
  if (column.sticky) {
    return ColumnPriority.CRITICAL;
  }

  // Always visible columns
  if (column.responsive?.alwaysVisible) {
    return ColumnPriority.CRITICAL;
  }

  // Checkbox column (id is empty string)
  if (column.id === '') {
    return ColumnPriority.CRITICAL;
  }

  // Expandable columns (typically main identifier)
  if (column.isExpandable) {
    return ColumnPriority.HIGH;
  }

  // Status columns
  if (
    column.id === 'status' ||
    column.label === 'Status' ||
    column.id === 'attackSurface'
  ) {
    return ColumnPriority.MEDIUM_HIGH;
  }

  // Date columns
  if (
    column.cell === 'date' ||
    column.id === 'created' ||
    column.id === 'visited'
  ) {
    return ColumnPriority.LOW;
  }

  // Tags and metadata
  if (column.id === 'tags' || column.id === 'origin') {
    return ColumnPriority.LOW;
  }

  // Default to medium priority
  return ColumnPriority.MEDIUM;
}

/**
 * Phase 1: Compress expandable columns to their minWidth.
 * Returns total width after compression and updated columns.
 */
function compressExpandableColumns<TData>(
  columns: ColumnWithVisibility<TData>[]
): { totalWidth: number; columns: ColumnWithVisibility<TData>[] } {
  let totalWidth = 0;

  const compressedColumns = columns.map(col => {
    // Get minimum width for this column
    const minWidth =
      col.fixedWidth || col.isExpandable?.minWidth || col.minWidth || 100;

    // Mark expandable columns as compressed
    const isCompressed = Boolean(col.isExpandable);

    totalWidth += minWidth;

    return {
      ...col,
      computedWidth: minWidth,
      isCompressed,
    };
  });

  return { totalWidth, columns: compressedColumns };
}

/**
 * Phase 2: Hide columns by priority (low first) until total width fits.
 * Never hides CRITICAL priority columns.
 */
function hideColumnsByPriority<TData>(
  columns: ColumnWithVisibility<TData>[],
  availableWidth: number
): { columns: ColumnWithVisibility<TData>[]; isResponsive: boolean } {
  // Sort by priority (low priority first = hide first)
  const sortedColumns = [...columns].sort((a, b) => {
    // Primary sort: priority (ascending - low priority first)
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    // Secondary sort: original index (descending - right columns first)
    return b.originalIndex - a.originalIndex;
  });

  // Calculate current width
  let currentWidth = sortedColumns.reduce(
    (sum, col) => sum + (col.computedWidth || 0),
    0
  );

  let hiddenCount = 0;

  // Hide columns until fits
  for (const column of sortedColumns) {
    if (currentWidth <= availableWidth) {
      break; // Fits now, stop hiding
    }

    // Never hide CRITICAL priority columns
    if (column.priority >= ColumnPriority.CRITICAL) {
      continue;
    }

    // Hide this column
    column.isVisible = false;
    currentWidth -= column.computedWidth || 0;
    hiddenCount++;
  }

  return {
    columns: columns, // Return in original order
    isResponsive: hiddenCount > 0,
  };
}
